/**
 * 003 — evidence-backed review of one bank movement. Extracted from the
 * prototype's cashflow page (behaviour unchanged) and extended with partner
 * attribution, return-debit pairing and suggestion prefill (`suggestionId`).
 * Nothing here changes an invoice or expense.
 */
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, GlassPanel, Input } from '../ui';
import { ApiError } from '../../services/api';
import {
  CLASSIFICATION_LABELS,
  PARTNER_CLASSIFICATIONS,
  bankApi,
  bankKeys,
  day,
  reviewSideEffectKeys,
  money,
  type Allocation,
  type Suggestion,
} from '../../lib/bank-api';
import type { FlowDocument } from './types';

export type ReviewTarget = {
  id: string;
  version: number;
  bookingDate: string;
  valueDate: string;
  amountCents: number;
  description: string;
  classification: string;
  reviewEvidence: string | null;
  allocations: Allocation[];
  partnerId?: string | null;
  pairedTransactionId?: string | null;
};

/**
 * Human-readable message of an API error. Handles the app's error shape
 * (`{ message }`) and zod-validator 400s (`{ success: false, error: { issues } }`).
 */
export function errorText(error: unknown): string {
  if (error instanceof ApiError) {
    const data = error.data as
      | { message?: string; error?: { issues?: { message?: string; path?: (string | number)[] }[] } }
      | null
      | undefined;
    if (data?.message) return data.message;
    const issues = data?.error?.issues ?? [];
    if (issues.length)
      return `Eingabe ungültig: ${issues
        .map((i) => (i.path?.length ? `${i.path.join('.')}: ${i.message ?? ''}` : (i.message ?? '')))
        .join('; ')}`;
    return error.message;
  }
  return error instanceof Error ? error.message : 'Unbekannter Fehler';
}

type Draft = { key: string; amount: string; evidence: string };

const toDraft = (a: Allocation): Draft => ({
  key: `${a.kind}:${a.documentId}`,
  amount: (a.amountCents / 100).toFixed(2).replace('.', ','),
  evidence: a.evidence,
});

function mergeTargets(existing: Allocation[], suggestion: Suggestion): Draft[] {
  const merged = existing.map((a) => ({ ...a }));
  for (const t of suggestion.targets) {
    const hit = merged.find((a) => a.kind === t.kind && a.documentId === t.documentId);
    if (hit) hit.amountCents += t.amountCents;
    else
      merged.push({
        kind: t.kind,
        documentId: t.documentId,
        amountCents: t.amountCents,
        evidence: `Vorschlag: ${suggestion.signals.map((s) => s.label).join('; ')}`,
      });
  }
  return merged.map(toDraft);
}

export function ReviewEditor({
  target,
  suggestion,
  documents,
  partners,
  pairCandidates,
  onClose,
  onSaved,
}: {
  target: ReviewTarget;
  /** Prefill from a suggestion; its id is sent so the snapshot lands in the history. */
  suggestion?: Suggestion | null;
  documents: FlowDocument[];
  partners: { id: string; displayName: string; active?: boolean }[];
  /** Opposite-sign movements of the same amount (for Rücklastschrift pairing). */
  pairCandidates: { id: string; bookingDate: string; amountCents: number; label: string }[];
  onClose: () => void;
  onSaved: (message: string) => void;
}) {
  const fromSuggestion = suggestion
    ? suggestion.kind === 'classification' || suggestion.kind === 'debit_return_pair'
      ? {
          classification: suggestion.classification ?? 'unknown',
          // A classification covers the open remainder: confirmed allocations stay
          // (as in the server-side bulk path). Only a return pair carries none.
          allocations: suggestion.kind === 'debit_return_pair' ? ([] as Draft[]) : target.allocations.map(toDraft),
        }
      : {
          classification: 'business',
          // Suggestions cover only the open remainder: keep confirmed allocations and add
          // the targets — merged by document, as the server-side bulk confirmation does.
          allocations: mergeTargets(target.allocations, suggestion),
        }
    : null;
  const [classification, setClassification] = useState(fromSuggestion?.classification ?? target.classification);
  const [evidence, setEvidence] = useState(
    suggestion ? `Vorschlag übernommen (${suggestion.level}): ${suggestion.signals.map((s) => s.label).join('; ')}` : (target.reviewEvidence ?? '')
  );
  const [allocations, setAllocations] = useState<Draft[]>(fromSuggestion?.allocations ?? target.allocations.map(toDraft));
  const [partnerId, setPartnerId] = useState(suggestion?.partnerId ?? target.partnerId ?? '');
  const [pairedId, setPairedId] = useState(suggestion?.pairedTransactionId ?? target.pairedTransactionId ?? '');
  const [error, setError] = useState('');
  // Optimistic locking: the version the reviewer saw when opening the editor. A later
  // refetch must not silently bump it, or a concurrent review would be overwritten.
  const [baseVersion] = useState(target.version);
  const ref = useRef<globalThis.HTMLDivElement>(null);
  const client = useQueryClient();
  useEffect(() => {
    ref.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    ref.current?.focus();
  }, []);
  const needsPartner = PARTNER_CLASSIFICATIONS.includes(classification);
  const needsPair = classification === 'debit_return';
  const save = useMutation({
    mutationFn: async () => {
      const mapped = needsPair
        ? []
        : allocations.map((a) => {
            const [kind, documentId] = a.key.split(':') as ['invoice' | 'expense', string];
            const amount = Number(a.amount.replace(/\./g, '').replace(',', '.')) * 100;
            if (!Number.isFinite(amount) || Math.abs(amount - Math.round(amount)) > 0.000001)
              throw new Error('Betrag mit maximal zwei Nachkommastellen eingeben');
            return { kind, documentId, amountCents: Math.round(amount), evidence: a.evidence };
          });
      return bankApi.review(target.id, {
        expectedVersion: baseVersion,
        classification: classification as never,
        evidence,
        allocations: mapped,
        ...(needsPartner && partnerId ? { partnerId } : {}),
        ...(needsPair && pairedId ? { pairedTransactionId: pairedId } : {}),
        ...(suggestion ? { suggestionId: suggestion.id } : {}),
      });
    },
    onSuccess: () => {
      for (const queryKey of reviewSideEffectKeys) void client.invalidateQueries({ queryKey });
      onSaved('Zuordnung mit Evidenz gespeichert.');
    },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 409) {
        void client.invalidateQueries({ queryKey: bankKeys.all });
        setError('Bankbuchung inzwischen geprüft; neu laden. Die Ansicht wurde aktualisiert.');
      } else setError(errorText(e));
    },
  });
  // A counterpart that is not in the current view cannot be inspected: never submit it blind.
  const pairHidden = needsPair && Boolean(pairedId) && !pairCandidates.some((c) => c.id === pairedId);
  const invalid =
    evidence.trim().length < 5 ||
    (needsPartner && !partnerId) ||
    (needsPair && !pairedId) ||
    pairHidden ||
    (!needsPair && allocations.some((a) => !a.key || a.evidence.trim().length < 5 || !a.amount)) ||
    save.isPending;
  return (
    <GlassPanel className="bank-no-print" data-testid="bank-review-editor">
      <div ref={ref} tabIndex={-1}>
        <h2 className="font-semibold">Buchung prüfen: {money(target.amountCents)}</h2>
        <p className="my-2 whitespace-pre-line text-sm">
          Buchung {day(target.bookingDate)} · Valuta {day(target.valueDate)}
          <br />
          {target.description}
        </p>
        {suggestion && (
          <p className="text-sm text-white/70">
            Vorschlag ({suggestion.level}) vorbelegt — bitte prüfen und ggf. anpassen.
          </p>
        )}
      </div>
      <div className="grid gap-3 md:grid-cols-2 mt-3">
        <label className="text-sm">
          Klassifikation
          <select
            className="block w-full rounded border border-white/10 bg-surface p-2"
            value={classification}
            onChange={(e) => setClassification(e.target.value)}
          >
            {Object.entries(CLASSIFICATION_LABELS).map(([value, label]) => (
              <option value={value} key={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        {needsPartner && (
          <label className="text-sm">
            Gesellschafter
            <select
              aria-label="Gesellschafter"
              className="block w-full rounded border border-white/10 bg-surface p-2"
              value={partnerId}
              onChange={(e) => setPartnerId(e.target.value)}
            >
              <option value="">Gesellschafter wählen</option>
              {partners.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.displayName}
                </option>
              ))}
            </select>
          </label>
        )}
        {needsPair && (
          <label className="text-sm">
            Gegenbuchung (Rücklastschrift)
            <select
              aria-label="Gegenbuchung"
              className="block w-full rounded border border-white/10 bg-surface p-2"
              value={pairedId}
              onChange={(e) => setPairedId(e.target.value)}
            >
              <option value="">Gegenbuchung wählen</option>
              {pairCandidates.map((c) => (
                <option key={c.id} value={c.id}>
                  {day(c.bookingDate)} · {money(c.amountCents)} · {c.label}
                </option>
              ))}
            </select>
            {pairHidden && (
              <span role="alert" className="mt-1 block text-amber-200" data-testid="bank-pair-hidden">
                Die vorgeschlagene Gegenbuchung liegt außerhalb des gewählten Zeitraums oder Kontos — Zeitraum erweitern,
                um sie vor dem Speichern zu prüfen.
              </span>
            )}
          </label>
        )}
      </div>
      <label className="block text-sm mt-3">
        Evidenz / Begründung
        <Input
          value={evidence}
          onChange={(e) => setEvidence(e.target.value)}
          placeholder="Nachvollziehbare Evidenz; Betrag allein genügt nicht"
        />
      </label>
      {!needsPair && (
        <>
          <p className="my-3 text-sm text-white/70">
            Teil- und Sammelzahlungen können mehrere Belege zuordnen. Betrag jeweils mit Vorzeichen der Buchung;
            Belegdatum, Zahlungstag und Status der Belege werden dadurch nicht verändert.
          </p>
          {allocations.map((a, i) => (
            <div className="flex flex-wrap gap-2 my-2" key={i}>
              <select
                aria-label="Beleg"
                className="max-w-full border border-white/10 rounded bg-surface p-2"
                value={a.key}
                onChange={(e) => setAllocations((v) => v.map((x, j) => (j === i ? { ...x, key: e.target.value } : x)))}
              >
                <option value="">Beleg auswählen</option>
                {documents.map((d) => (
                  <option key={`${d.kind}:${d.id}`} value={`${d.kind}:${d.id}`}>
                    {d.kind === 'invoice' ? 'Rechnung' : 'Ausgabe'} {d.number ?? d.id} ({d.netTotal} / {d.grossTotal}{' '}
                    {d.currency})
                  </option>
                ))}
              </select>
              <Input
                aria-label="Zuordnungsbetrag EUR"
                value={a.amount}
                placeholder="EUR mit Vorzeichen"
                onChange={(e) => setAllocations((v) => v.map((x, j) => (j === i ? { ...x, amount: e.target.value } : x)))}
              />
              <Input
                aria-label="Zuordnungsevidenz"
                value={a.evidence}
                placeholder="Evidenz zur Belegzuordnung"
                onChange={(e) => setAllocations((v) => v.map((x, j) => (j === i ? { ...x, evidence: e.target.value } : x)))}
              />
              <Button onClick={() => setAllocations((v) => v.filter((_, j) => j !== i))}>Entfernen</Button>
            </div>
          ))}
        </>
      )}
      {error && (
        <p role="alert" className="mt-3 text-sm text-red-300">
          {error}
        </p>
      )}
      <div className="flex flex-wrap gap-3 mt-3">
        {!needsPair && (
          <Button onClick={() => setAllocations((v) => [...v, { key: '', amount: '', evidence: '' }])}>
            Beleg hinzufügen
          </Button>
        )}
        <Button disabled={invalid} onClick={() => save.mutate()} data-testid="bank-review-save">
          Evidenz speichern
        </Button>
        <Button onClick={onClose}>Schließen</Button>
      </div>
    </GlassPanel>
  );
}
